<template>
  <div class="speakers-container" data-cont="small">
    <BaseSectionHeading :data="data[Object.keys(data)[0]].data" />

    <div class="speakers-container__list">
      <a
        class="speakers-container__item"
        v-for="(speaker, index) in speakers"
        :key="index"
        :js-link="`/${getCity}/speakers${speaker.path}`"
        @mouseenter="lottieLoop(index, true)"
        @mouseleave="lottieLoop(index, false)"
        :js-lottieref="`speakers_${index}`"
        @click.prevent="goToSpeaker"
        v-if="speaker !== undefined"
      >
        <BasePicture
          class="speakers-container__image"
          :data="Object.assign(speaker, { tagImg: false, rotation: data[Object.keys(data)[index]].data.rotation })"
          :lottieBg="'speakers'"
          :lottieRef="`speakers_${index}`"
        />
      </a>
    </div>

    <BaseSectionHeading :data="sponsorData" />
    <Sponsor :data="sponsorData" />

    <transition @enter="enter" @leave="leave">
      <SpeakerModal
        v-if="$route.params.speaker !== undefined"
        :style="{ transform: `translate3d(0, ${translate}px, 0)`}"
        :speakers="speakers"
        :speakersPositions="speakersPositions"
      />
    </transition>
  </div>
</template>

<script>
import anime from 'animejs';
import lottie from 'lottie-web';
import { mapGetters } from 'vuex';
import store from '@/store/store';
import { qs, isMobileDevice, qsa } from '@/scripts/utils';

import BaseSectionHeading from '@/components/BaseSectionHeading';
import BasePicture from '@/components/BasePicture';
import BaseButtonFilled from '@/components/BaseButtonFilled';
import SpeakerModal from '@/components/SpeakerModal';
import Sponsor from '@/components/Sponsor';

import orderBy from 'lodash/orderBy';

export default {
  name: 'speakersContainer',

  components: {
    BaseSectionHeading,
    BasePicture,
    SpeakerModal,
    Sponsor,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },

    translate: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      orderedComponents: {},
      sponsorData: {},
      speakersPositions: [],
    };
  },

  computed: {
    ...mapGetters(['getCity']),

    ...mapGetters({ currentSpeaker: 'getSpeakerName' }),

    speakers() {
      const speakers = [];

      this.orderedComponents.forEach((component, index) => {
        const speakerId = component.data.speaker;

        if (index < this.orderedComponents.length - 1) {
          speakers.push(store.state.speakers[this.getCity][speakerId]);
        }
      });

      return speakers;
    },
  },

  created() {
    this.orderedComponents = orderBy(this.data, ['order']);
    this.sponsorData = this.orderedComponents[this.orderedComponents.length - 1].data;
  },

  mounted() {
    const speakersItems = qsa('.speakers-container__item');

    this.$nextTick(() => {
      speakersItems.forEach((el, i) => {
        this.speakersPositions.push(Math.round(el.getBoundingClientRect().top + window.lerpedScrollY -
          ((window.innerHeight - el.getBoundingClientRect().height) /2)));
      });
    });
  },

  methods: {
    goToSpeaker(event) {
      const el = event.target;
      const url = el.getAttribute('js-link');
      el.removeAttribute('js-anim');

      this.$eventHub.$emit('loading');

      this.$router.push(url);
    },

    enter(el, done) {
      el.style.opacity = 0;

      const img = qs('.image--current', el);
      const bgRed = qs('.speaker-modal__bg-red', el);
      bgRed.style.transformOrigin = '50% 100%';
      const tX = img.getBoundingClientRect().width + 100;
      const title = qs('h3', el);
      const subtitle = qs('h4', el);
      const paragraph = qs('p', el);
      const list = qs('ul', el);
      const delay = 500;

      const basicTimeline = anime.timeline();

      basicTimeline.add({
        targets: el,
        duration: 10,
        opacity: [0, 1],
        easing: 'easeInOutSine',
        offset: 1000,
        begin: () => {
          this.$eventHub.$emit('setTo', 0);
        },
      })
      .add({
        targets: bgRed,
        duration: 650,
        scaleY: [0, 1],
        easing: 'easeOutExpo',
        offset: 1600,
      })
      .add({
        targets: img,
        duration: 1000,
        opacity: [0, 1],
        translateX: [tX, 0],
        rotateZ: ['5deg', '0deg'],
        easing: [0.25, 0.87, 0.43, 1],
        offset: 2000,
      })
      .add({
        targets: title,
        duration: 800,
        opacity: [0, 1],
        translateX: [160, 0],
        easing: [0.25, 0.87, 0.43, 1],
        offset: 2000,
      })
      .add({
        targets: subtitle,
        duration: 800,
        opacity: [0, 1],
        translateX: [160, 0],
        easing: [0.25, 0.87, 0.43, 1],
        offset: 2050,
      })
      .add({
        targets: paragraph,
        duration: 800,
        opacity: [0, 1],
        translateX: [160, 0],
        easing: [0.25, 0.87, 0.43, 1],
        offset: 2100,
      })
      .add({
        targets: list,
        duration: 800,
        opacity: [0, 1],
        translateX: [160, 0],
        easing: [0.25, 0.87, 0.43, 1],
        offset: 2150,
      });

      basicTimeline.finished.then(() => {
        document.body.classList.remove('wait');
        done();
      });
    },

    leave(el, done) {
      this.$eventHub.$emit('loading');

      anime({
        targets: el,
        duration: 450,
        opacity: [1, 0],
        easing: 'easeInOutSine',
        delay: 1000,
        complete: () => {
          done();
        },
      });

      anime({
        targets: '.speakers-container__list',
        duration: 650,
        opacity: [0, 1],
        easing: 'easeInOutSine',
        delay: 1800,
        complete: () => {
          document.body.classList.remove('wait');
        },
      });
    },

    lottieLoop(index, enter) {
      if (isMobileDevice()) return;

      const lotties = lottie.getRegisteredAnimations();
      const speaker = lotties.find(el => el.name === `speakers_${index}`);

      const img = qs(`[js-lottieref="speakers_${index}"] .picture__image-bg`);
      const rotation = qs(`[js-lottieref="speakers_${index}"] .picture__container`).getAttribute('js-rotation');


      if (speaker !== undefined) {
        if (enter) {
          speaker.playSegments([26, 50], true);
          this.timeout = setTimeout(() => {
            speaker.resetSegments();
            speaker.loop = true;
            speaker.play();
          }, 500);

          const basicTimelineEnter = anime.timeline();

          basicTimelineEnter.add({
            targets: img,
            duration: 400,
            easing: 'easeInOutSine',
            scale: [0.8, 0.7],
            rotateZ: [`${rotation}deg`, '0deg'],
          });
        } else {
          clearTimeout(this.timeout);
          // speaker.resetSegments();
          speaker.loop = false;
          speaker.playSegments([speaker.currentRawFrame, 25], true);

          const basicTimelineLeave = anime.timeline();

          basicTimelineLeave.add({
            targets: img,
            duration: 400,
            easing: 'easeInOutSine',
            scale: [0.7, 0.8],
            rotateZ: ['0deg', `${rotation}deg`],
          });
        }
      }
    },
  },

  beforeDestroy() {
    const lotties = lottie.getRegisteredAnimations();
    const speakers = lotties.filter(elem => elem.name !== '');
    speakers.forEach((speaker) => { speaker.destroy(); });
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/utils.scss';

.speakers-container {
  position: relative;
  width: 100%;

  padding: 80px 20px;

  @include mq(mobile) {
    padding: 80px 30px;
  }

  @include mq(ipadP) {
    padding: 80px 40px;
  }

  @include mq(ipadL) {
    padding: 80px 80px;
  }

  &__list {
    position: relative;
    width: 100%;

    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-flow: row wrap;

    margin-bottom: 60px;

    @include mq(mobileL) {
      justify-content: space-between;
    }

    @include mq(ipadP) {
      margin-bottom: 80px;
    }

    @include mq(ds) {
      margin-bottom: 100px;
    }
  }

  &__item {
    display: inline-block;
    width: 100%;
    max-width: 360px;
    flex: 0 0 100%;
    margin: 60px 0;

    pointer-events: auto;
    cursor: pointer;

    @include mq(mobileL) {
      width: 50%;
      flex: 0 0 50%;
      max-width: 275px;
      margin: 0;

      &:nth-child(even) {
        margin-top: 160px;
      }
    }

    @include mq(ipadP) {
      max-width: 300px;
    }

    @include mq(ipadL) {
      max-width: 360px;
    }
  }

  &__image {
    display: inline-block;
    position: relative;

    @include aspect-ratio(3, 4);

    pointer-events: none;
  }
}
</style>
